/**
 * UPT-2124 J4c (spec `web/SPEC-UPT-2124-J4.md` §3 « MockVoiceSession ») — la GRAMMAIRE du simulateur vocal pour les outils d'interface : « ouvre … »,
 * « clique sur … », « mets … dans … », « mets … comme … », « remplis … avec … », « choisis … dans … », « coche … », « descends », « reviens », « annule »,
 * « qu'est-ce qu'il y a à l'écran ». Le simulateur n'a pas de modèle : cette table fait l'office de la compréhension du modèle vocal, en français et en
 * anglais, et appelle les MÊMES outils `ui_*` par le même chemin que la session réelle (`executeVoiceTool` → registre).
 *
 * Pur : du texte en entrée, un appel d'outil en sortie (ou `null`). Les valeurs gardent la casse et les accents du texte dit.
 */
import { normalizeUiText } from './uiRiskTable';

export interface UiGrammarCall {
  name: string;
  args: Record<string, unknown>;
  /**
   * Appels de repli, dans l'ordre, tant que le précédent ne trouve rien (`not_found`) : « ouvre X » = le menu d'abord, puis l'élément de l'écran, puis
   * (« le prêt de Nora ») le complément du nom (« Nora »).
   */
  fallbacks?: { name: string; args: Record<string, unknown> }[];
}

const READ_PHRASES = [
  "qu'est ce qu'il y a a l'ecran",
  "qu'est ce qu'il y a sur l'ecran",
  "qu'est ce qu'on voit a l'ecran",
  "lis l'ecran",
  "lis moi l'ecran",
  "decris l'ecran",
  'que vois tu',
  'what is on the screen',
  "what's on the screen",
  'what do you see',
  'read the screen',
];
const SCROLL_PHRASES: Readonly<Record<string, 'up' | 'down' | 'top' | 'bottom'>> = {
  descends: 'down',
  descend: 'down',
  'plus bas': 'down',
  'vers le bas': 'down',
  'scroll down': 'down',
  'go down': 'down',
  monte: 'up',
  remonte: 'up',
  'plus haut': 'up',
  'vers le haut': 'up',
  'scroll up': 'up',
  'go up': 'up',
  'tout en bas': 'bottom',
  'en bas': 'bottom',
  'va en bas': 'bottom',
  'scroll to the bottom': 'bottom',
  bottom: 'bottom',
  'tout en haut': 'top',
  'en haut': 'top',
  'va en haut': 'top',
  'scroll to the top': 'top',
  top: 'top',
};
const BACK_PHRASES = ['reviens', 'reviens en arriere', 'retour', 'retourne', 'page precedente', 'go back', 'back', 'previous page'];
export const UNDO_PHRASES = ['annule', 'annule ca', 'annule le dernier', 'annule le remplissage', 'defais', 'defais ca', 'undo', 'undo that', 'revert'];

/**
 * Verbe dit → noms du bouton visé, dans l'ordre d'essai : le nom français, puis l'anglais (l'interface peut être dans l'une ou l'autre langue ; le
 * modèle réel traduit tout seul, le simulateur essaie les deux). L'acteur résout ensuite le bouton nommé et le classe.
 */
const BUTTON_VERBS: Readonly<Record<string, readonly string[]>> = {
  enregistre: ['Enregistrer', 'Save'],
  enregistrer: ['Enregistrer', 'Save'],
  sauvegarde: ['Sauvegarder', 'Save'],
  sauvegarder: ['Sauvegarder', 'Save'],
  valide: ['Valider', 'Validate'],
  valider: ['Valider', 'Validate'],
  envoie: ['Envoyer', 'Send'],
  envoyer: ['Envoyer', 'Send'],
  soumets: ['Soumettre', 'Submit'],
  soumettre: ['Soumettre', 'Submit'],
  save: ['Save', 'Enregistrer'],
  submit: ['Submit', 'Soumettre'],
  send: ['Send', 'Envoyer'],
  supprime: ['Supprimer', 'Delete'],
  supprimer: ['Supprimer', 'Delete'],
  efface: ['Effacer', 'Erase'],
  effacer: ['Effacer', 'Erase'],
  ecarte: ['Écarter', 'Discard'],
  ecarter: ['Écarter', 'Discard'],
  archive: ['Archiver', 'Archive'],
  archiver: ['Archiver', 'Archive'],
  revoque: ['Révoquer', 'Revoke'],
  paye: ['Payer', 'Pay'],
  payer: ['Payer', 'Pay'],
  achete: ['Acheter', 'Buy'],
  acheter: ['Acheter', 'Buy'],
  depose: ['Déposer', 'Deposit'],
  deposer: ['Déposer', 'Deposit'],
  delete: ['Delete', 'Supprimer'],
  remove: ['Remove', 'Supprimer'],
  erase: ['Erase', 'Effacer'],
  pay: ['Pay', 'Payer'],
  buy: ['Buy', 'Acheter'],
};
const BUTTON_VERB_PATTERN = new RegExp(
  `^(${Object.keys(BUTTON_VERBS).join('|')})(?:\\s+(?:ca|ceci|le formulaire|la fiche|les modifications|ce pret|ce dossier|cette ligne|it|this|the form|changes))?$`
);

const DETERMINER = "(?:(?:les|le|la|une|un|the|my|mes|mon|ma)\\s+|l['’]\\s*)?";
const LEADING_DETERMINER = /^(?:(?:les|le|la|une|un|the|my|mes|mon|ma)\s+|l['’]\s*)?/i;
const NOUN_ROLES: Readonly<Record<string, string | undefined>> = {
  carte: undefined,
  card: undefined,
  etape: undefined,
  step: undefined,
  onglet: 'tab',
  tab: 'tab',
  ligne: 'row',
  row: 'row',
  bouton: 'button',
  button: 'button',
  lien: 'link',
  link: 'link',
};
const NOUN_PATTERN = new RegExp(`^(?:(${Object.keys(NOUN_ROLES).join('|')})\\s+)(.+)$`, 'i');

const unquote = (text: string): string =>
  text
    .trim()
    .replace(/[.!?…]+$/, '')
    .trim()
    .replace(/^[«"'‘“]+|[»"'’”]+$/g, '')
    .trim();

/** « la carte Concevoir », « l'onglet Historique » → le nom (et le rôle quand le nom du type en donne un). */
const targetOf = (text: string): { name: string; role?: string } => {
  const cleaned = unquote(text).replace(new RegExp(`^${DETERMINER}`, 'i'), '');
  const noun = NOUN_PATTERN.exec(normalizeUiText(cleaned));
  if (!noun) {
    return { name: unquote(cleaned) };
  }
  const rest = cleaned.slice(cleaned.length - noun[2].length).replace(new RegExp(`^${DETERMINER}`, 'i'), '');
  return { name: unquote(rest), role: NOUN_ROLES[noun[1]] };
};

const FILL_PATTERNS: readonly { pattern: RegExp; value: number; label: number }[] = [
  { pattern: /^(?:mets|met|mettre|saisis|saisir|[ée]cris|[ée]crire|entre|entrer|inscris|put|set|enter|type|write)\s+(.+?)\s+(?:dans|in|into)\s+(.+)$/i, value: 1, label: 2 },
  { pattern: /^(?:mets|met|mettre|put|set)\s+(.+?)\s+(?:comme|en tant que|as)\s+(.+)$/i, value: 1, label: 2 },
  { pattern: /^(?:remplis|remplir|fill(?:\s+in)?)\s+(.+?)\s+(?:avec|par|with)\s+(.+)$/i, value: 2, label: 1 },
  { pattern: /^(?:mets|met|mettre|set|change|modifie)\s+(.+?)\s+(?:[àa]|to|sur)\s+(.+)$/i, value: 2, label: 1 },
];

const LABEL_PREFIX = /^(?:(?:les|le|la|une|un|the|my)\s+|l['’]\s*)?(?:champ|field|case|zone)\s+(?:de\s+|du\s+|des\s+)?/i;

const labelOf = (text: string): string =>
  unquote(text)
    .replace(new RegExp(`^${DETERMINER}`, 'i'), '')
    .replace(LABEL_PREFIX, '')
    .replace(new RegExp(`^${DETERMINER}`, 'i'), '')
    .replace(/\s+(?:field|champ|case)$/i, '');

/**
 * L'appel d'outil d'interface que dit la phrase (déjà privée de l'interpellation « Géode, »), ou `null` : ce n'est pas un geste d'écran, le
 * simulateur poursuit avec les autres outils et le copilote. « annule » n'est pas traité ici (il garde son sens « non » d'une confirmation et
 * « annule la phase » de l'Idée) : voir `isUndoPhrase`.
 */
export const uiToolOf = (text: string): UiGrammarCall | null => {
  const trimmed = text.trim().replace(/[.!?…]+$/, '').trim();
  const normalized = normalizeUiText(trimmed).replace(/[-\s]+/g, ' ');
  if (!normalized) return null;

  if (READ_PHRASES.includes(normalized)) return { name: 'ui_read', args: {} };
  if (SCROLL_PHRASES[normalized]) return { name: 'ui_scroll', args: { to: SCROLL_PHRASES[normalized] } };
  const scrollTo = /^(?:descends|d[ée]file|scroll|va|aller)\s+(?:jusqu['’]\s*(?:[àa]|au|aux)|to|vers)\s+(.+)$/i.exec(trimmed);
  if (scrollTo) return { name: 'ui_scroll', args: { to: targetOf(scrollTo[1]).name } };
  if (BACK_PHRASES.includes(normalized)) return { name: 'ui_back', args: {} };

  const buttonVerb = BUTTON_VERB_PATTERN.exec(normalized);
  if (buttonVerb) {
    const [first, ...others] = BUTTON_VERBS[buttonVerb[1]];
    return { name: 'ui_click', args: { name: first }, fallbacks: others.map((name) => ({ name: 'ui_click', args: { name } })) };
  }

  const step = /^(?:passe|passer|va|aller|go)\s+(?:[àa]|au|aux|to)\s+(?:l['’]\s*)?(?:[ée]tape|step)\s+(.+)$/i.exec(trimmed);
  if (step) return { name: 'ui_click', args: { name: unquote(step[1]) } };

  const click = /^(?:clique|cliquer|clic|appuie|appuyer|presse|click|press|tap)(?:\s+(?:sur|on))?\s+(.+)$/i.exec(trimmed);
  if (click) {
    const target = targetOf(click[1]);
    return { name: 'ui_click', args: target.role ? { name: target.name, role: target.role } : { name: target.name } };
  }

  const check = /^(?:(coche|cocher|check|tick)|(d[ée]coche|d[ée]cocher|uncheck|untick))\s+(.+)$/i.exec(trimmed);
  if (check) return { name: 'ui_fill', args: { label: labelOf(check[3]), value: check[1] ? 'true' : 'false' } };

  const select = /^(?:choisis|choisir|s[ée]lectionne|s[ée]lectionner|select|choose|pick)\s+(.+?)\s+(?:dans|in|from|parmi)\s+(.+)$/i.exec(trimmed);
  if (select) return { name: 'ui_select', args: { label: labelOf(select[2]), option: unquote(select[1]) } };

  for (const { pattern, value, label } of FILL_PATTERNS) {
    const match = pattern.exec(trimmed);
    if (match) {
      return { name: 'ui_fill', args: { label: labelOf(match[label]), value: unquote(match[value]) } };
    }
  }

  const open = /^(?:ouvre|ouvrir|ouvrez|va\s+(?:[àa]|au|aux)|aller\s+(?:[àa]|au|aux)|open|go\s+to)\s+(.+)$/i.exec(trimmed);
  if (open) {
    const target = targetOf(open[1]);
    const click = (name: string) => ({ name: 'ui_click', args: target.role ? { name, role: target.role } : { name } });
    // « le prêt de Nora » : le complément du nom (« Nora ») est souvent tout ce que la ligne affiche.
    const complement = /\b(?:de|du|des|d['’]|of)\s+(.+)$/i.exec(target.name);
    const clicks = [click(target.name), ...(complement ? [click(unquote(complement[1]))] : [])];
    // Un nom précédé d'un type d'élément (« la carte Concevoir », « l'onglet Historique ») vise l'écran, pas le menu.
    const names = unquote(open[1]).replace(LEADING_DETERMINER, '');
    if (target.role !== undefined || /^(?:carte|card|[ée]tape|step)\b/i.test(names)) {
      return { ...clicks[0], fallbacks: clicks.slice(1) };
    }
    return { name: 'ui_navigate', args: { destination: names }, fallbacks: clicks };
  }
  return null;
};

/** « annule », « undo » : le dernier remplissage de Géode (quand ni une confirmation ni la phase d'une Idée n'attendent le mot). */
export const isUndoPhrase = (text: string): boolean => UNDO_PHRASES.includes(normalizeUiText(text.trim().replace(/[.!?…]+$/, '')).replace(/[-\s]+/g, ' '));
